<template>
    <div
        class="pdf-content-scroll"
        ref="scrollContainer"
        @scroll="handleScroll"
    >
        <div
            v-for="pageNum in pageNums"
            :key="pageNum"
            :id="`page-${pageNum}`"
            ref="pageRefs"
            class="pdf-page"
        >
            <!-- <VuePdfEmbed
                v-if="pageVisibility[pageNum] && isPageInWindow(pageNum)"
                class="pdf-page-content"
                ref="pdfEmbed"
                :source="pdf"
                :text-layer="true"
                :page="pageNum"
                :width="pdfWidth"
                :height="pdfHeight"
                :rotation="rotate"
                @error="$emit('error', $event)"
            /> -->
            <!-- <div
                v-else
                class="page-placeholder"
                :style="{
                    width: `${pdfWidth}px`,
                    height: `${pdfHeight}px`,
                    marginInline: 'auto',
                }"
            >
                <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div> -->
            <VuePdfEmbed
                class="pdf-page-content"
                ref="pdfEmbed"
                :source="pdf"
                :text-layer="true"
                :page="pageNum"
                :width="pdfWidth"
                :height="pdfHeight"
                :rotation="rotate"
                @error="$emit('error', $event)"
            />
        </div>
    </div>
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed';
import 'vue-pdf-embed/dist/styles/annotationLayer.css';
import 'vue-pdf-embed/dist/styles/textLayer.css';

export default {
    name: 'PdfCanvas',
    components: {
        VuePdfEmbed,
    },
    props: {
        pdf: { required: true },
        pages: { type: Number, required: true },
        pdfWidth: { type: Number, required: true },
        pdfHeight: { type: Number, required: true },
        rotate: { type: Number, default: 0 },
        currentPage: { type: Number, default: 1 },
        fitMode: { type: String, default: null },
        scale: { type: Number, default: 1.0 },
        maxRenderedPages: { type: Number, default: 6 },
    },
    data() {
        return {
            pageIntersectionObserver: null,
            pageVisibility: {},
            scrollTimeout: null,
            isUserScrolling: false,
            // Store scroll position for scale changes to preserve scroll position
            lastScrollPosition: { x: 0 },
            // Store the first visible element info for zoom preservation
            firstVisibleElement: null,
            //rotating flag
            isRotating: false,
        };
    },
    emits: ['page-changed', 'error'],
    computed: {
        pageNums() {
            return this.pages ? [...Array(this.pages + 1).keys()].slice(1) : [];
        },
        renderStart() {
            if (!this.pages) return 1;
            const half = Math.floor(this.maxRenderedPages / 2);
            return Math.max(1, this.currentPage - half);
        },
        renderEnd() {
            return Math.min(
                this.pages || 0,
                this.renderStart + this.maxRenderedPages - 1
            );
        },
    },
    watch: {
        pageNums(newPageNums) {
            if (newPageNums.length > 0) {
                this.pageVisibility = { [newPageNums[0]]: true };
                setTimeout(() => this.resetPageIntersectionObserver(), 100);
            }
        },
        currentPage(newPage, oldPage) {
            if (!this.isUserScrolling && newPage !== oldPage) {
                this.scrollToPage(newPage);
            }
        },
        // Watch for scale changes to preserve scroll position
        scale(newScale, oldScale) {
            if (newScale !== oldScale) {
                if (this.fitMode === 'height') {
                    this.$nextTick(() => this.scrollToPage(this.currentPage));
                } else {
                    this.handleScaleChange(newScale, oldScale);
                }
            }
        },
        rotate(newVal, oldVal) {
            if (newVal !== oldVal) {
                const keepPage = this.currentPage;
                this.isRotating = true;
                this.$nextTick(() => {
                    this.scrollToPage(keepPage);
                    // ensure scroll handler runs with the guard still on, then release
                    this.$nextTick(() => (this.isRotating = false));
                });
            }
        },
    },
    methods: {
        resetPageIntersectionObserver() {
            this.pageIntersectionObserver?.disconnect();

            this.pageIntersectionObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const index = this.$refs.pageRefs.indexOf(
                                entry.target
                            );
                            const pageNum = this.pageNums[index];
                            this.pageVisibility = {
                                ...this.pageVisibility,
                                [pageNum]: true,
                            };
                        }
                    });
                }
            );

            this.$refs.pageRefs?.forEach((element) => {
                this.pageIntersectionObserver.observe(element);
            });
        },

        handleScroll() {
            if (this.isRotating) return;
            this.isUserScrolling = true;

            if (this.scrollTimeout) clearTimeout(this.scrollTimeout);

            this.updateCurrentPageFromScroll();

            // Only debounce the isUserScrolling flag reset
            this.scrollTimeout = setTimeout(() => {
                this.isUserScrolling = false;
            }, 100);
        },

        updateCurrentPageFromScroll() {
            if (this.isRotating) return;
            const scrollContainer = this.$refs.scrollContainer;
            if (!scrollContainer) return;

            const containerStyle = window.getComputedStyle(scrollContainer);
            const topPadding = parseFloat(containerStyle.paddingTop) || 0;

            const viewportTop = scrollContainer.scrollTop + topPadding;
            const SNAP_THRESHOLD = 12; // standard threshold for page snapping

            let newPage = 1;

            for (let page = 1; page <= this.pages; page++) {
                const pageElement = document.getElementById(`page-${page}`);
                if (!pageElement) continue;

                const pageTop = pageElement.offsetTop;

                if (pageTop - viewportTop <= SNAP_THRESHOLD) {
                    newPage = page;
                } else {
                    break;
                }
            }

            if (this.currentPage !== newPage) {
                this.$emit('page-changed', newPage);
            }
        },

        scrollToPage(pageNumber) {
            if (pageNumber < 1 || pageNumber > this.pages) return;

            this.$nextTick(() => {
                const pageElement = document.getElementById(
                    `page-${pageNumber}`
                );
                const scrollContainer = this.$refs.scrollContainer;
                if (!pageElement || !scrollContainer) return;

                const containerStyle = window.getComputedStyle(scrollContainer);
                const pageStyle = window.getComputedStyle(pageElement);

                const topAllowance = parseFloat(containerStyle.paddingTop) || 4;
                const bottomAllowance =
                    parseFloat(pageStyle.marginBottom) || 10;

                const pageTop = pageElement.offsetTop;
                const pageBottom = pageTop + pageElement.offsetHeight;
                const containerH = scrollContainer.clientHeight;
                const maxScrollTop = scrollContainer.scrollHeight - containerH;

                let desiredTop = pageTop - topAllowance;

                const neededTopForBottom =
                    pageBottom + bottomAllowance - containerH;
                if (desiredTop + containerH < pageBottom + bottomAllowance) {
                    desiredTop = neededTopForBottom;
                }

                desiredTop = Math.max(0, Math.min(desiredTop, maxScrollTop));

                scrollContainer.scrollTo({
                    top: desiredTop,
                    left: 0,
                    behavior: 'auto',
                });
            });
        },

        handleScaleChange(newScale, oldScale) {
            this.captureScrollState();

            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    this.restoreScrollPosition(newScale, oldScale);
                });
            });
        },

        captureScrollState() {
            const scrollContainer = this.$refs.scrollContainer;
            if (!scrollContainer) return;
            const scrollLeft = scrollContainer.scrollLeft;
            this.lastScrollPosition = { x: scrollLeft };
            // Find the first visible element in the viewport
            this.firstVisibleElement = this.findFirstVisibleElement();
        },

        findFirstVisibleElement() {
            const scrollContainer = this.$refs.scrollContainer;
            if (!scrollContainer) return null;

            const style = window.getComputedStyle(scrollContainer);
            const topPadding = parseFloat(style.paddingTop) || 0;

            const viewportTop = scrollContainer.scrollTop + topPadding;
            const viewportBottom =
                scrollContainer.scrollTop + scrollContainer.clientHeight;

            for (let page = 1; page <= this.pages; page++) {
                const pageElement = document.getElementById(`page-${page}`);
                if (pageElement) {
                    const pageTop = pageElement.offsetTop;
                    const pageBottom = pageTop + pageElement.offsetHeight;

                    if (
                        pageBottom > scrollContainer.scrollTop &&
                        pageTop < viewportBottom
                    ) {
                        const offsetFromPageTop = Math.max(
                            0,
                            viewportTop - pageTop
                        );
                        return {
                            pageNumber: page,
                            offsetFromTop: Math.min(
                                offsetFromPageTop,
                                pageElement.offsetHeight
                            ),
                        };
                    }
                }
            }

            return null;
        },

        restoreScrollPosition(newScale, oldScale) {
            const scrollContainer = this.$refs.scrollContainer;
            if (!scrollContainer || !this.firstVisibleElement) return;

            const scaleRatio = newScale / oldScale;
            const visibleElement = this.firstVisibleElement;

            // Get the updated position of the previously visible element
            const pageElement = document.getElementById(
                `page-${visibleElement.pageNumber}`
            );
            if (!pageElement) return;

            const style = window.getComputedStyle(scrollContainer);
            const topPadding = parseFloat(style.paddingTop) || 0;

            const newPageTop = pageElement.offsetTop;
            const scaledOffsetFromTop =
                visibleElement.offsetFromTop * scaleRatio;

            const newScrollTop = newPageTop + scaledOffsetFromTop - topPadding;
            const newScrollLeft = this.lastScrollPosition.x * scaleRatio;

            scrollContainer.scrollTo({
                top: Math.max(0, newScrollTop),
                left: Math.max(0, newScrollLeft),
                behavior: 'auto',
            });

            this.firstVisibleElement = null;
        },

        sanitizeFilename(name) {
            const fallback = 'document.pdf';
            if (!name) return fallback;
            const cleaned = String(name)
                .replace(/[\\/:*?"<>|]+/g, '_')
                .trim();
            return cleaned || fallback;
        },

        async printPdf(title) {
            try {
                const refs = this.$refs.pdfEmbed;
                const pdfEmbed = Array.isArray(refs)
                    ? refs.find(Boolean)
                    : refs;

                if (!pdfEmbed || typeof pdfEmbed.print !== 'function') {
                    this.$alertStore.pushError('Failed to print PDF.');
                    return Promise.resolve(false);
                }

                return await pdfEmbed.print(
                    72,
                    this.sanitizeFilename(title),
                    true
                );
            } catch (error) {
                this.$alertStore.pushError(
                    error.message || 'Failed to print PDF.'
                );
                return Promise.resolve(false);
            }
        },

        async downloadPdf(title) {
            try {
                const refs = this.$refs.pdfEmbed;
                const pdfEmbed = Array.isArray(refs)
                    ? refs.find(Boolean)
                    : refs;

                if (!pdfEmbed || typeof pdfEmbed.download !== 'function') {
                    this.$alertStore.pushError('Failed to download PDF.');
                    return Promise.resolve(false);
                }

                return await pdfEmbed.download(this.sanitizeFilename(title));
            } catch (error) {
                this.$alertStore.pushError(
                    error.message || 'Failed to download PDF.'
                );
                return Promise.resolve(false);
            }
        },

        isPageInWindow(pageNum) {
            return pageNum >= this.renderStart && pageNum <= this.renderEnd;
        },
    },
    mounted() {
        if (this.pageNums.length > 0) {
            this.pageVisibility = { [this.currentPage]: true };
            this.$nextTick(() => this.resetPageIntersectionObserver());
        }
    },
    beforeUnmount() {
        this.pageIntersectionObserver?.disconnect();
        if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
    },
};
</script>

<style scoped>
.pdf-content-scroll {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
    height: 0;
    position: relative;
    padding-top: var(--scroll-container-padding-top);
}

.pdf-page {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    margin-top: var(--page-gap-top);
    margin-bottom: var(--page-gap-bottom);
    margin-left: var(--page-margin-inline);
    margin-right: var(--page-margin-inline);

    inline-size: calc(100% - (2 * var(--page-margin-inline)));

    min-height: 0;
}

.pdf-page:first-child {
    margin-top: 0;
}

.pdf-page-content {
    margin-inline: auto;
}

.page-placeholder {
    background-color: var(--white);
    border: 1px solid var(--neutral-400);
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
